import type { DataPoint } from "~/tools/data-point";
import { Lazy } from "~/tools/lazy";
import { type NonEmptyArray, isNonEmptyArray } from "~/tools/non-empty-array";
import type { Point } from "~/tools/point";

/**
 * A continuous run of non-null data points, pre-formatted as SVG
 * coordinate strings for `<polyline>` and `<polygon>` elements.
 */
export class Segment {
	/**
	 * Splits data points into segments at null-value gaps.
	 * Each segment holds only the consecutive non-null points
	 * converted to SVG coordinates via {@link convert}.
	 */
	public static fromDataPoints(
		points: ReadonlyArray<DataPoint>,
		convert: (dataPoint: DataPoint) => undefined | Point,
		bottom: number,
	): Array<Segment> {
		const segments: Array<Segment> = [];
		let current: Array<Point> = [];

		for (const point of points.map(convert)) {
			if (point != null) {
				current.push(point);
			} else if (isNonEmptyArray(current)) {
				segments.push(new Segment(current, bottom));
				current = [];
			}
		}

		if (isNonEmptyArray(current)) {
			segments.push(new Segment(current, bottom));
		}

		return segments;
	}

	/** SVG `points` attribute value for `<polyline>` (line only). */
	public readonly linePoints = Lazy.create(() => this.points.map(({ x, y }) => `${x},${y}`).join(" "));

	/** SVG `points` attribute value for `<polygon>` (closed area shape). */
	public readonly areaPoints = Lazy.create(() => {
		const [first] = this.points;
		const last = this.points.at(-1) ?? first;
		const line = this.linePoints.get();

		return `${line} ${last.x},${this.bottom} ${first.x},${this.bottom}`;
	});

	private constructor(
		private readonly points: NonEmptyArray<Point>,
		private readonly bottom: number,
	) {}
}
